import { PredefinedDataListRevisionDataResponse } from 'api/actions/predefined-data-list-revision-data/predefined-data-list-revision-data-response';
import {
  RevisionPredefinedDataOption,
  RevisionPredefinedDataOrigin,
  RevisionPredefinedDataSelection,
} from 'components/inputs/revision-predefined-data/types';

const ONE_TIME_GROUP = 'Jednorazové';

/**
 * Sorts predefined data options using the existing user/global ordering.
 */
export function sortRevisionPredefinedDataOptions(
  a: Pick<RevisionPredefinedDataOption, 'origin' | 'value'>,
  b: Pick<RevisionPredefinedDataOption, 'origin' | 'value'>
) {
  if (a.origin === b.origin) {
    return a.value.localeCompare(b.value);
  }

  if (a.origin === 'one-time') {
    return -1;
  }

  if (b.origin === 'one-time') {
    return 1;
  }

  if (a.origin === 'global') {
    return 1;
  }

  if (b.origin === 'global') {
    return -1;
  }

  return a.origin.localeCompare(b.origin);
}

/**
 * Normalizes API response data into select options.
 */
export function normalizeRevisionPredefinedDataOptions(
  response: PredefinedDataListRevisionDataResponse
): RevisionPredefinedDataOption[] {
  return response
    .map(({ key, value, origin }) => ({
      value: key,
      label: key,
      description: value ?? '',
      group: origin === 'global' ? 'Globálne' : 'Vlastné',
      origin: origin as RevisionPredefinedDataOrigin,
      source: 'catalog' as const,
    }))
    .sort(sortRevisionPredefinedDataOptions);
}

/**
 * Creates a synthetic one-time option that Mantine can render as selected.
 */
export function createOneTimeRevisionPredefinedDataOption(
  selection: Pick<RevisionPredefinedDataSelection, 'name' | 'description'>
): RevisionPredefinedDataOption {
  return {
    value: selection.name,
    label: selection.name,
    description: selection.description ?? '',
    group: ONE_TIME_GROUP,
    origin: 'one-time',
    source: 'one-time',
  };
}

/**
 * Returns whether the option collection already contains the provided key.
 */
export function hasRevisionPredefinedDataExactMatch(
  options: Pick<RevisionPredefinedDataOption, 'value'>[],
  query: string
): boolean {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (!normalizedQuery) {
    return false;
  }

  return options.some((option) => option.value.trim().toLocaleLowerCase() === normalizedQuery);
}
